import React, { createContext, useState, useEffect, useContext } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

export const VisibilityContext = createContext();

export const useVisibility = () => {
  const context = useContext(VisibilityContext);
  if (!context) {
    throw new Error('useVisibility must be used within a VisibilityProvider');
  }
  return context;
};

export const VisibilityProvider = ({ children }) => {
  const [visibilityStates, setVisibilityStates] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch visibility states for all students
  useEffect(() => {
    const fetchVisibilityStates = async () => {
      try {
        const visibilityRef = collection(db, 'visibility');
        const querySnapshot = await getDocs(visibilityRef);
        
        const states = {};
        querySnapshot.forEach((doc) => {
          if (doc.id !== 'financialSection') { // Skip the global setting
            states[doc.id] = doc.data().isVisible;
          }
        });

        // Get global financial section visibility
        const globalRef = doc(db, 'visibility', 'financialSection');
        const globalSnap = await getDoc(globalRef);
        const globalVisible = globalSnap.exists() ? globalSnap.data().isVisible : true;

        setVisibilityStates({
          global: globalVisible,
          students: states
        });
      } catch (error) {
        console.error('Error fetching visibility states:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVisibilityStates();
  }, []);

  // Update visibility state for a specific student
  const updateStudentVisibility = async (studentId, isVisible) => {
    try {
      const docRef = doc(db, 'visibility', studentId);
      await setDoc(docRef, { isVisible });
      
      setVisibilityStates(prev => ({
        ...prev,
        students: {
          ...prev.students,
          [studentId]: isVisible
        }
      }));
    } catch (error) {
      console.error('Error updating student visibility state:', error);
    }
  };

  // Update global financial section visibility
  const updateGlobalVisibility = async (isVisible) => {
    try {
      const docRef = doc(db, 'visibility', 'financialSection');
      await setDoc(docRef, { isVisible });
      
      setVisibilityStates(prev => ({
        ...prev,
        global: isVisible
      }));
    } catch (error) {
      console.error('Error updating global visibility state:', error);
    }
  };

  // Check if financial section is visible for a specific student
  const isFinancialSectionVisible = (studentId) => {
    if (loading) return false;
    // If global visibility is false, nothing is visible
    if (!visibilityStates.global) {
      return false;
    }
    // Check student-specific visibility, default to true if not set
    return visibilityStates.students[studentId] ?? true;
  };

  // Раньше здесь весь сайт — включая главную и вход — ждал, пока из базы
  // придут настройки видимости финансов, и показывал голое «Loading...».
  // Настройка касается одного блока в кабинете; пока она не пришла, блок
  // скрыт (isFinancialSectionVisible возвращает false), остальное рисуется сразу.

  return (
    <VisibilityContext.Provider 
      value={{ 
        isFinancialSectionVisible,
        updateGlobalVisibility,
        updateStudentVisibility,
        globalVisibility: visibilityStates.global
      }}
    >
      {children}
    </VisibilityContext.Provider>
  );
};